import { Box, Button, TextField, useTheme } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';
import { QueryLegalPersonForIdentityCheckFlowRes, QueryLegalPersonRes } from '~/api/types';
import { Page } from '~/components/ui/page';
import { Body1, Title } from '~/components/ui/typography';
import { formatCpfCnpj } from '~/helpers/formatters';
import { isValidCpfOrCnpj } from '~/helpers/validators';
import { LoadingScreen } from './loading';
import { isAxiosError } from 'axios';
import config from '~/config';

export function LegalPersonSelectionScreen<T extends QueryLegalPersonRes | QueryLegalPersonForIdentityCheckFlowRes>({
  queryLegalPerson,
  onLegalPersonQueried,
  title,
  description,
  buttonText,
}: Props<T>) {
  const [reqStatus, setReqStatus] = useState<ReqStatus>('idle');
  const [doc, setDoc] = useState('');
  const captchaContainerRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const isValidDocument = isValidCpfOrCnpj(doc);
  const reqErrorMessage = isReqError(reqStatus) ? getErrorMessageFromReqStatus(reqStatus) : '';

  useEffect(() => {
    const preFilledDoc = new URLSearchParams(window.location.search).get('mainDocument');
    if (preFilledDoc) setDoc(preFilledDoc);
  }, []);

  const queryLegalPersonByDoc = async () => {
    if (!isValidDocument) return setReqStatus('invalid-document');
    try {
      setReqStatus('in-progress');
      const res = await queryLegalPerson(doc);

      // the developments are already narrowed by the backend to the ones served at the current host. A
      // document with a single development or none gets no legal person, so it is never reported as not found
      if (res.legalPerson?.developments.length === 0) return setReqStatus('not-found');
      else return onLegalPersonQueried(res);
    } catch (error: unknown) {
      if (isWafCaptchaResponse(error)) {
        // O WAF do CloudFront pede um CAPTCHA antes de consultar o documento:
        // desenha o quebra-cabeça aqui e repete a consulta quando resolvido.
        setReqStatus('captcha-required');
        return;
      }
      console.error(error);
      if (isAxiosError(error) && error.response?.status === 429) {
        setReqStatus('too-many-requests');
      } else {
        setReqStatus('unknown-error');
      }
    }
  };

  useEffect(() => {
    if (reqStatus !== 'captcha-required') return;
    const container = captchaContainerRef.current;
    const captcha = window.AwsWafCaptcha;
    const apiKey = config.WAF_CAPTCHA_API_KEY;
    if (!container || !captcha || !apiKey) {
      console.error('WAF pediu CAPTCHA, mas o SDK ou a chave não estão disponíveis neste build');
      setReqStatus('unknown-error');
      return;
    }
    const applyBrandColor = () => applyCaptchaBrandColor(container, theme);
    captcha.renderCaptcha(container, {
      apiKey,
      // Só pt-BR, sem o seletor de idioma; o título vem da nossa tela.
      defaultLocale: 'pt-BR',
      disableLanguageSelector: true,
      skipTitle: true,
      dynamicWidth: true,
      onLoad: applyBrandColor,
      onSuccess: () => {
        setReqStatus('idle');
        queryLegalPersonByDoc();
      },
      onError: (error) => {
        console.error(error);
        setReqStatus('unknown-error');
      },
    });
    applyBrandColor();
    // queryLegalPersonByDoc muda a cada render; o efeito só deve rodar ao entrar no estado.
  }, [reqStatus]);

  if (reqStatus === 'in-progress') {
    return <LoadingScreen message="Buscando empreendimentos..." />;
  }

  if (reqStatus === 'captcha-required') {
    return (
      <Page innerWrapperSx={{ width: { xs: '100%', sm: '800px' } }}>
        <Title>Confirme que você não é um robô</Title>
        <Body1>Selecione as imagens pedidas e clique em Confirmar.</Body1>
        {/* Com dynamicWidth o SDK dimensiona o quebra-cabeça pela largura do
            elemento host (entre 180 e 640 px) e desenha as imagens nesse
            tamanho, sem borrar. O host precisa ocupar a largura do cartão: num
            contêiner flex ele encolhia ao conteúdo e ficava em 320 px. */}
        <Box ref={captchaContainerRef} sx={{ mt: 3, width: '100%' }} />
      </Page>
    );
  }

  return (
    <Page innerWrapperSx={{ width: { xs: '100%', sm: '800px' } }}>
      <Title>{title}</Title>
      <Body1>{description}</Body1>
      <TextField
        label="Seu CPF ou CNPJ"
        value={formatCpfCnpj(doc)}
        onChange={(e) =>
          setDoc(
            e.target.value
              .toUpperCase()
              .replace(/[^0-9A-Z]/g, '')
              .substring(0, 14),
          )
        }
        onKeyDown={(e) => {
          if (e.key === 'Enter') queryLegalPersonByDoc();
        }}
        error={isReqError(reqStatus)}
        helperText={reqErrorMessage}
        autoFocus
        fullWidth
        variant="filled"
        inputProps={{ inputMode: 'numeric' }}
        sx={{ mb: 3, mt: 1 }}
      />
      <Button
        disabled={!isValidDocument}
        onClick={queryLegalPersonByDoc}
        fullWidth
        variant="contained"
        sx={{ height: '50px', fontWeight: 'bold', fontSize: { xs: '0.9rem', sm: '1rem' } }}
      >
        {buttonText}
      </Button>
    </Page>
  );
}

function getErrorMessageFromReqStatus(reqStatus: ReqError): string {
  const errorMap: Record<ReqError, string> = {
    'invalid-document': 'CPF/CNPJ inválido',
    'not-found': 'Não há contratos vinculados com este documento',
    'unknown-error': 'Erro desconhecido. Tente novamente.',
    'too-many-requests': 'Muitas tentativas. Tente novamente mais tarde.',
  };
  return errorMap[reqStatus];
}

type ReqStatus = 'idle' | 'in-progress' | 'captcha-required' | ReqError;
type ReqError = 'invalid-document' | 'not-found' | 'too-many-requests' | 'unknown-error';
function isReqError(e: unknown): e is ReqError {
  return ['invalid-document', 'not-found', 'too-many-requests', 'unknown-error'].includes(e as string);
}

/**
 * Pinta o botão "Confirmar" do widget com a cor primária do tema (a da
 * construtora, no white-label). O widget é o elemento <awswaf-captcha>, com
 * shadow DOM aberto e o botão na classe .btn-primary (laranja #FF9902 da AWS);
 * um <style> acrescentado ao shadow root sobrepõe o CSS do SDK. Não é
 * suportado pela AWS: se uma versão nova do SDK trocar a classe, o botão volta
 * a laranja e nada mais muda. Idempotente: roda no render e a cada onLoad.
 */
function applyCaptchaBrandColor(container: HTMLElement, theme: Theme) {
  const shadow = container.querySelector('awswaf-captcha')?.shadowRoot;
  if (!shadow || shadow.querySelector('#cub-captcha-brand')) return;
  const { main, dark, contrastText } = theme.palette.primary;
  const style = document.createElement('style');
  style.id = 'cub-captcha-brand';
  style.textContent = [
    `.btn-primary { background-color: ${main}; border-color: ${main}; color: ${contrastText}; border-radius: ${theme.shape.borderRadius}px; }`,
    `.btn-primary:hover { background-color: ${dark}; border-color: ${dark}; }`,
    `#root *:focus { outline-color: ${main}; }`,
  ].join('\n');
  shadow.appendChild(style);
}

/**
 * Resposta da ação CAPTCHA do AWS WAF: HTTP 405 com o header x-amzn-waf-action.
 * O BFF nunca responde 405 por conta própria, então o header é só confirmação.
 */
function isWafCaptchaResponse(error: unknown): boolean {
  if (!isAxiosError(error) || error.response?.status !== 405) return false;
  const action = error.response.headers?.['x-amzn-waf-action'];
  return action === undefined || String(action).toLowerCase() === 'captcha';
}

interface Props<T extends QueryLegalPersonRes | QueryLegalPersonForIdentityCheckFlowRes> {
  title: string;
  description: string;
  buttonText: string;
  queryLegalPerson: (doc: string) => Promise<T>;
  onLegalPersonQueried: (res: T) => void;
}
